import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { 
  AlertTriangle, Upload, Loader2, FileText, Download, Calendar, 
  CheckCircle2, XCircle, Clock, Info, Mail
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR");
  } catch {
    return dateStr;
  }
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  } catch {
    return dateStr;
  }
};

/**
 * Carte "Résiliation" affichée dans la fiche contrat
 * Visible uniquement si le contrat a une résiliation en cours ou est résilié
 */
export function ResiliationClientCard({ contrat, onRefresh }) {
  const isEnCours = contrat.statut === "resiliation_en_cours";
  const isResilie = contrat.statut === "resilier" && contrat.date_effet_resiliation;
  
  if (!isEnCours && !isResilie && !contrat.date_demande_resiliation) {
    return null;
  }
  
  const token = localStorage.getItem("token");
  
  const handleDownloadDemandeResiliation = async () => {
    try {
      const response = await axios.get(
        `${API}/api/engagements/contrats/${contrat.id}/demande-resiliation/document`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `demande_resiliation_${contrat.societe || contrat.prestataire}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };
  
  return (
    <Card className={`border-2 ${isEnCours ? 'border-orange-400 bg-orange-50' : 'border-gray-400 bg-gray-100'}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <AlertTriangle className={isEnCours ? "w-5 h-5 text-orange-600" : "w-5 h-5 text-gray-600"} />
          Résiliation
          <Badge className={isEnCours ? "bg-orange-500 text-white" : "bg-gray-500 text-white"}>
            {isEnCours ? "En cours" : "Effectuée"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Date de demande</p>
            <p className="font-medium">{formatDate(contrat.date_demande_resiliation)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Date d'effet</p>
            <p className={`font-bold ${isEnCours ? 'text-orange-700' : 'text-gray-700'}`}>
              {formatDate(contrat.date_effet_resiliation)}
            </p>
          </div>
        </div>
        
        {contrat.reference_ar_resiliation && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Référence AR</p>
            <p className="font-mono text-sm">{contrat.reference_ar_resiliation}</p>
          </div>
        )}
        
        {contrat.signataire_demande_resiliation && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Signataire</p>
            <p className="text-sm">{contrat.signataire_demande_resiliation}</p>
          </div>
        )}
        
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Préavis appliqué</p>
          <p className="text-sm">{contrat.duree_preavis_resiliation_mois || 3} mois</p>
        </div>
        
        {contrat.has_demande_resiliation_document && (
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={handleDownloadDemandeResiliation}
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger la demande client
          </Button>
        )}
        
        {isEnCours && (
          <Alert className="bg-orange-100 border-orange-300">
            <Clock className="w-4 h-4 text-orange-600" />
            <AlertDescription className="text-orange-800 text-sm">
              Les prélèvements seront stoppés au <strong>{formatDateLong(contrat.date_effet_resiliation)}</strong>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Bouton + Modal pour importer une demande de résiliation client
 */
export function ResiliationClientUpload({ contrat, onSuccess }) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [confirmData, setConfirmData] = useState({
    date_demande_resiliation: "",
    date_effet_resiliation: "",
    reference_ar: "",
    signataire: "",
    duree_preavis_mois: contrat?.duree_preavis_resiliation_mois || 3
  });
  const [fileData, setFileData] = useState(null);
  
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("token");
  
  // Ne pas afficher si déjà en résiliation
  if (contrat?.statut === "resiliation_en_cours" || contrat?.statut === "resilier") {
    return null;
  }
  
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier le type
    const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Type de fichier non supporté. Acceptés: PDF, PNG, JPG");
      return;
    }
    
    // Vérifier la taille (10 Mo max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux. Maximum: 10 Mo");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(
        `${API}/api/engagements/contrats/${contrat.id}/demande-resiliation/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      const result = response.data;
      setExtractionResult(result);
      
      // Pré-remplir les données de confirmation
      setConfirmData({
        date_demande_resiliation: result.extraction?.date_lettre_client || new Date().toISOString().split('T')[0],
        date_effet_resiliation: result.calcul_date_effet?.date_effet_resiliation || "",
        reference_ar: result.extraction?.reference_ar || "",
        signataire: result.extraction?.nom_signataire 
          ? `${result.extraction.nom_signataire}${result.extraction.fonction_signataire ? ' - ' + result.extraction.fonction_signataire : ''}`
          : "",
        duree_preavis_mois: result.contrat?.duree_preavis_mois || 3
      });
      
      setFileData(result.file_data);
      setShowUploadModal(false);
      setShowConfirmModal(true);
      
      toast.success("Document analysé avec succès");
      
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse du document");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  
  const handleConfirm = async () => {
    if (!confirmData.date_demande_resiliation || !confirmData.date_effet_resiliation) {
      toast.error("Veuillez renseigner les dates");
      return;
    }
    
    setConfirming(true);
    
    try {
      // Construire les paramètres de requête
      const params = new URLSearchParams();
      if (fileData?.base64) {
        params.append("file_base64", fileData.base64);
        params.append("file_filename", fileData.filename);
        params.append("file_content_type", fileData.content_type);
      }
      
      const response = await axios.post(
        `${API}/api/engagements/contrats/${contrat.id}/demande-resiliation/confirm?${params.toString()}`,
        confirmData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      toast.success("Résiliation validée avec succès");
      
      if (response.data.notification_email_envoyee) {
        toast.info("Email de notification envoyé aux destinataires");
      }
      
      setShowConfirmModal(false);
      setExtractionResult(null);
      setFileData(null);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
    } catch (error) {
      console.error("Confirm error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setConfirming(false);
    }
  };
  
  return (
    <>
      {/* Bouton d'import */}
      <Button 
        variant="outline" 
        className="border-orange-300 text-orange-700 hover:bg-orange-50"
        onClick={() => setShowUploadModal(true)}
      >
        <Upload className="w-4 h-4 mr-2" />
        Importer demande de résiliation
      </Button>
      
      {/* Modal d'upload */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-600" />
              Importer une demande de résiliation
            </DialogTitle>
            <DialogDescription>
              Importez la lettre recommandée de résiliation envoyée par le client.
              L'IA extraira automatiquement les informations.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-sm">
                <strong>Contrat concerné:</strong> {contrat?.nom_contrat}<br/>
                <strong>Client:</strong> {contrat?.societe || contrat?.prestataire}
              </AlertDescription>
            </Alert>
            
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
                  <p className="text-sm text-gray-600">Analyse en cours...</p>
                </div>
              ) : (
                <>
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    Cliquez ou glissez-déposez le fichier
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PDF, PNG ou JPG (max 10 Mo)
                  </p>
                </>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                className="hidden"
                onChange={handleFileSelect}
                disabled={uploading}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUploadModal(false)} disabled={uploading}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal de confirmation */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Validation de la demande de résiliation
            </DialogTitle>
            <DialogDescription>
              Vérifiez et corrigez si nécessaire les informations extraites avant de valider.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Extraction IA */}
            {extractionResult?.extraction && (
              <Alert className={extractionResult.extraction.confidence_score > 0.7 ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}>
                <Info className={extractionResult.extraction.confidence_score > 0.7 ? "w-4 h-4 text-green-600" : "w-4 h-4 text-amber-600"} />
                <AlertDescription className="text-sm">
                  <strong>Extraction IA</strong> - Confiance: {Math.round((extractionResult.extraction.confidence_score || 0) * 100)}%
                  {extractionResult.extraction.societe_cliente && (
                    <span className="block mt-1">Société détectée: {extractionResult.extraction.societe_cliente}</span>
                  )}
                </AlertDescription>
              </Alert>
            )}
            
            {/* Calcul date d'effet */}
            {extractionResult?.calcul_date_effet && (
              <Alert className="bg-blue-50 border-blue-200">
                <Calendar className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  {extractionResult.calcul_date_effet.explication}
                </AlertDescription>
              </Alert>
            )}
            
            <Separator />
            
            {/* Formulaire de validation */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de la demande *</Label>
                <Input 
                  type="date"
                  value={confirmData.date_demande_resiliation}
                  onChange={(e) => setConfirmData({...confirmData, date_demande_resiliation: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Date inscrite sur la lettre du client</p>
              </div>
              
              <div>
                <Label>Référence AR</Label>
                <Input 
                  value={confirmData.reference_ar}
                  onChange={(e) => setConfirmData({...confirmData, reference_ar: e.target.value})}
                  placeholder="N° de suivi recommandé"
                />
              </div>
              
              <div>
                <Label>Date d'effet de la résiliation *</Label>
                <Input 
                  type="date"
                  value={confirmData.date_effet_resiliation}
                  onChange={(e) => setConfirmData({...confirmData, date_effet_resiliation: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Date anniversaire après respect du préavis</p>
              </div>
              
              <div>
                <Label>Durée du préavis (mois)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="12"
                  value={confirmData.duree_preavis_mois}
                  onChange={(e) => setConfirmData({...confirmData, duree_preavis_mois: parseInt(e.target.value) || 3})}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Signataire de la demande</Label>
                <Input 
                  value={confirmData.signataire}
                  onChange={(e) => setConfirmData({...confirmData, signataire: e.target.value})}
                  placeholder="Nom - Fonction"
                />
              </div>
            </div>
            
            <Alert className="bg-orange-50 border-orange-200">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              <AlertDescription className="text-orange-800 text-sm">
                <strong>Actions automatiques à la validation :</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Mise à jour du statut du contrat en "Résiliation en cours"</li>
                  <li>Génération automatique de la lettre d'acceptation (.docx)</li>
                  <li>Envoi d'une notification email aux destinataires configurés</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="ghost" 
              onClick={() => {
                setShowConfirmModal(false);
                setExtractionResult(null);
                setFileData(null);
              }} 
              disabled={confirming}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button 
              onClick={handleConfirm} 
              disabled={confirming}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {confirming ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Valider la résiliation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * Bouton pour télécharger la lettre d'acceptation
 */
export function DownloadLettreAcceptation({ contratId, lettreId, filename }) {
  const [downloading, setDownloading] = useState(false);
  const token = localStorage.getItem("token");
  
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/api/engagements/contrats/${contratId}/lettre-acceptation/${lettreId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "lettre_acceptation.docx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Lettre téléchargée");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setDownloading(false);
    }
  };
  
  return (
    <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading}>
      {downloading ? (
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
      ) : (
        <Download className="w-4 h-4 mr-2" />
      )}
      Lettre d'acceptation
    </Button>
  );
}


export default { ResiliationClientCard, ResiliationClientUpload, DownloadLettreAcceptation };
